import {
  ACCOUNT_CODE_LINK_CSV_TEMPLATE_URL,
  ACCOUNT_CSV_TEMPLATE_URL,
  APPROVER_CSV_TEMPLATE_URL,
  BUDGET_CATEGORY_CSV_TEMPLATE_URL,
  BUDGET_CSV_TEMPLATE_URL,
  CATALOG_ITEM_CSV_TEMPLATE_URL,
  CATALOG_PERMISSION_CSV_TEMPLATE_URL,
  DEPARTMENT_CSV_TEMPLATE_URL,
  IMPORT_ACCOUNT_CODE_LINK_URL,
  IMPORT_ACCOUNT_URL,
  IMPORT_APPROVER_URL,
  IMPORT_BUDGET_CATEGORY_URL,
  IMPORT_BUDGET_URL,
  IMPORT_CATALOG_ITEM_URL,
  IMPORT_CATALOG_PERMISSION_URL,
  IMPORT_DEPARTMENT_URL,
  IMPORT_LOCATION_URL,
  IMPORT_PERMISSION_URL,
  IMPORT_PLANNED_BUDGET_URL,
  IMPORT_ROLE_URL,
  IMPORT_SHIPPING_ADDRESS_URL,
  IMPORT_VENDOR_URL,
  LOCATION_CSV_TEMPLATE_URL,
  PERMISSION_CSV_TEMPLATE_URL,
  PLANNED_BUDGET_CSV_TEMPLATE_URL,
  ROLE_CSV_TEMPLATE_URL,
  SHIPPING_ADDRESS_CSV_TEMPLATE_URL,
  VENDOR_CSV_TEMPLATE_URL,
} from '@webapp/api/ImportAPI'
import { ImportTypes } from '@webapp/models/enums'
import { USER_IMPORTER_OPTIONS } from '@webapp/platform/_unmigrated/Importer/constants'
import {
  SETTINGS_IMPORT_APPROVAL_ROUTING_GROUP_SECTION_DATA,
  SETTINGS_IMPORT_APPROVAL_ROUTING_GROUP_WITH_ADVANCED_TRIGGERS,
} from './ApprovalRoutingGroup'
import { SettingsImportLearnMoreLink } from '../../../components/SettingsImportRows/components/SettingsImportLearnMoreLink'
import messages from '../messages'
import {
  type SettingsImportSectionData,
  type SettingsImportSectionDataType,
} from '../types'

const SETTINGS_IMPORT_LOCATION_SECTION_DATA: SettingsImportSectionData = {
  type: ImportTypes.LOCATION,
  title: messages.primaryOrgCategoryHeader,
  description: {
    message: messages.primaryOrgCategoryDescription,
  },
  href: 'https://success.procurify.com/hc/en-us/articles/360046937353-How-to-Import-a-New-Location',
  modalTitle: messages.primaryOrgCategoryModalHeader,
  importerOptions: {
    importEndpoint: IMPORT_LOCATION_URL,
    templateEndpoint: LOCATION_CSV_TEMPLATE_URL,
    requiredFields: [
      'name',
      'currency_name',
      'phone',
      'company_label',
      'company_address',
      'company_city',
      'company_state_province',
      'company_postal_code',
      'company_country',
      'shipping_label',
      'shipping_address',
      'shipping_city',
      'shipping_state_province',
      'shipping_postal_code',
      'shipping_country',
    ],
    optionalFields: [
      'location_id',
      'email',
      'fax',
      'url',
      'locationTimezone',
      'external_id',
    ],
    fieldNameMapper: {
      location_id: 'Location ID',
      name: 'Location Name', // PTI-152 TODO: Find way to provide i18n values of these texts - we could just pass the msg obj and translate it in the component
      currency_name: 'Currency',
      external_id: 'External ID',
      phone: 'Phone',
      email: 'Email',
      fax: 'Fax',
      url: 'Website',
      company_label: 'Company Label',
      company_address: 'Company Address',
      company_city: 'Company City',
      company_state_province: 'Company State/Province',
      company_postal_code: 'Company Postal Code',
      company_country: 'Company Country',
      shipping_label: 'Shipping Label',
      shipping_address: 'Shipping Address',
      shipping_city: 'Shipping City',
      shipping_state_province: 'Shipping State/Province',
      shipping_postal_code: 'Shipping Postal Code',
      shipping_country: 'Shipping Country',
      locationTimezone: 'Timezone',
    },
  },
}

const SETTINGS_IMPORT_SHIPPING_ADDRESS_SECTION_DATA: SettingsImportSectionData =
  {
    type: ImportTypes.SHIPPING_ADDRESS,
    title: messages.shippingAddressHeader,
    description: {
      message: messages.shippingAddressDescription,
    },
    href: 'https://success.procurify.com/hc/en-us/articles/360048581794-How-to-Import-Shipping-Addresses',
    modalTitle: messages.shippingAddressModalHeader,
    importerOptions: {
      importEndpoint: IMPORT_SHIPPING_ADDRESS_URL,
      templateEndpoint: SHIPPING_ADDRESS_CSV_TEMPLATE_URL,
      requiredFields: [
        'location_id',
        'label',
        'address',
        'city',
        'state_province',
        'postal_code',
        'country',
      ],
      optionalFields: [],
      fieldNameMapper: {
        location_id: 'Location ID',
        label: 'Label',
        address: 'Address',
        city: 'City',
        state_province: 'State/Province',
        postal_code: 'Postal Code',
        country: 'Country',
      },
    },
  }

const SETTINGS_IMPORT_DEPARTMENT_SECTION_DATA: SettingsImportSectionData = {
  type: ImportTypes.DEPARTMENT,
  title: messages.secondaryOrgCategoryHeader,
  description: {
    message: messages.secondaryOrgCategoryDescription,
  },
  href: 'https://success.procurify.com/hc/en-us/articles/360046302534-How-to-Import-New-Departments',
  modalTitle: messages.secondaryOrgCategoryModalHeader,
  importerOptions: {
    importEndpoint: IMPORT_DEPARTMENT_URL,
    templateEndpoint: DEPARTMENT_CSV_TEMPLATE_URL,
    requiredFields: ['location_name', 'name'],
    optionalFields: ['external_id'],
    fieldNameMapper: {
      location_name: 'Location Name',
      name: 'Department Name',
      external_id: 'External ID',
    },
  },
}

export const SETTINGS_IMPORT_USER_SECTION_DATA: SettingsImportSectionData = {
  type: ImportTypes.USER,
  title: messages.userHeader,
  description: {
    message: messages.userDescription,
  },
  href: 'https://success.procurify.com/hc/en-us/articles/360006565834-Importing-Users',
  modalTitle: messages.userModalHeader,
  importerOptions: USER_IMPORTER_OPTIONS,
}

const SETTINGS_IMPORT_ROLE_SECTION_DATA: SettingsImportSectionData = {
  type: ImportTypes.ROLE,
  title: messages.roleHeader,
  description: {
    message: messages.roleDescription,
  },
  href: 'https://success.procurify.com/hc/en-us/articles/360006575974-Importing-Roles',
  modalTitle: messages.roleModalHeader,
  importerOptions: {
    importEndpoint: IMPORT_ROLE_URL,
    templateEndpoint: ROLE_CSV_TEMPLATE_URL,
    requiredFields: ['email', 'role'],
    optionalFields: ['location', 'department', 'action_on_duplicate'],
    fieldNameMapper: {
      email: 'Email',
      role: 'Role',
      location: 'Location',
      department: 'Department',
      action_on_duplicate: 'Action On Duplicate',
    },
  },
}

const SETTINGS_IMPORT_PERMISSION_SECTION_DATA: SettingsImportSectionData = {
  type: ImportTypes.PERMISSION,
  title: messages.permissionHeader,
  description: {
    message: messages.permissionDescription,
    values: {
      here1: (
        <SettingsImportLearnMoreLink
          key='here1'
          href='https://success.procurify.com/hc/en-us/articles/360049335573'
          linkMessage={messages.here}
        />
      ),
      here2: (
        <SettingsImportLearnMoreLink
          key='here2'
          href='https://success.procurify.com/hc/en-us/articles/360050439294-How-to-Import-User-Permissions'
          linkMessage={messages.here}
        />
      ),
    },
  },
  modalTitle: messages.permissionModalHeader,
  importerOptions: {
    importEndpoint: IMPORT_PERMISSION_URL,
    templateEndpoint: PERMISSION_CSV_TEMPLATE_URL,
    requiredFields: ['email', 'permission'],
    optionalFields: ['location', 'department', 'action_on_duplicate'],
    fieldNameMapper: {
      email: 'Email',
      permission: 'Permission',
      location: 'Location',
      department: 'Department',
      action_on_duplicate: 'Action On Duplicate',
    },
  },
}

const SETTINGS_IMPORT_APPROVER_SECTION_DATA: SettingsImportSectionData = {
  type: ImportTypes.APPROVER,
  title: messages.approverHeader,
  description: {
    message: messages.approverDescription,
  },
  href: 'https://success.procurify.com/hc/en-us/articles/360022247053-How-to-Import-Approval-Routing',
  modalTitle: messages.approverModalHeader,
  importerOptions: {
    importEndpoint: IMPORT_APPROVER_URL,
    templateEndpoint: APPROVER_CSV_TEMPLATE_URL,
    requiredFields: ['group_name', 'email', 'level', 'threshold'],
    optionalFields: ['action_on_duplicate'],
    fieldNameMapper: {
      group_name: 'Group Name',
      email: 'Email',
      level: 'Level',
      threshold: 'Threshold',
      action_on_duplicate: 'Action On Duplicate',
    },
  },
}

const SETTINGS_IMPORT_ACCOUNT_SECTION_DATA: SettingsImportSectionData = {
  type: ImportTypes.ACCOUNT,
  title: messages.accountHeader,
  description: {
    message: messages.accountDescription,
  },
  href: 'https://success.procurify.com/hc/en-us/articles/360024582934-How-to-Import-Account-Codes',
  modalTitle: messages.accountModalHeader,
  importerOptions: {
    importEndpoint: IMPORT_ACCOUNT_URL,
    templateEndpoint: ACCOUNT_CSV_TEMPLATE_URL,
    requiredFields: ['code', 'type', 'description', 'location', 'department'],
    optionalFields: ['subcode', 'subcode_type', 'subcode_description'],
    fieldNameMapper: {
      code: 'Account Code',
      type: 'Account Type',
      description: 'Account Description',
      location: 'Location Name',
      department: 'Department Name',
      subcode: 'Subcode',
      subcode_type: 'Subcode Type',
      subcode_description: 'Subcode Description',
    },
    fieldMatcher: {
      location: ['Location', 'Location Name'],
      department: ['Department', 'Department Name'],
    },
  },
}

const SETTINGS_IMPORT_ACCOUNT_CODE_LINK_SECTION_DATA: SettingsImportSectionData =
  {
    type: ImportTypes.ACCOUNT_CODE_LINK,
    title: messages.accountCodeLinkHeader,
    description: {
      message: messages.accountCodeLinkDescription,
    },
    href: 'https://success.procurify.com/hc/en-us/articles/11632798456599',
    modalTitle: messages.accountCodeLinkModalHeader,
    importerOptions: {
      importEndpoint: IMPORT_ACCOUNT_CODE_LINK_URL,
      templateEndpoint: ACCOUNT_CODE_LINK_CSV_TEMPLATE_URL,
      requiredFields: ['code', 'location', 'department'],
      optionalFields: ['action'],
      fieldNameMapper: {
        code: 'Account Code',
        location: 'Location Name',
        department: 'Department Name',
        action: 'Action On Duplicate',
      },
      fieldMatcher: {
        location: ['Location', 'Location Name'],
        department: ['Department', 'Department Name'],
        action: ['Action On Duplicate'],
      },
    },
  }

const SETTINGS_IMPORT_BUDGET_CATEGORY_SECTION_DATA: SettingsImportSectionData =
  {
    type: ImportTypes.BUDGET_CATEGORY,
    title: messages.budgetCategoryHeader,
    description: {
      message: messages.budgetCategoryDescription,
    },
    href: 'https://success.procurify.com/hc/en-us/articles/14153756478743-How-to-import-Budget-Categories',
    modalTitle: messages.budgetCategoryModalHeader,
    importerOptions: {
      importEndpoint: IMPORT_BUDGET_CATEGORY_URL,
      templateEndpoint: BUDGET_CATEGORY_CSV_TEMPLATE_URL,
      requiredFields: ['name'],
      optionalFields: [
        'location_name',
        'department_name',
        'account_code',
        'period',
        'customize_periods',
        'budget',
        'start_datetime',
        'end_datetime',
        'source_of_truth',
        'users',
        'action',
      ],
      fieldNameMapper: {
        name: 'Budget Category Name',
        location_name: 'Location Name',
        department_name: 'Department Name',
        account_code: 'Account Code',
        period: 'Period',
        customize_periods: 'Custom Period',
        budget: 'Budget',
        start_datetime: 'Start Date',
        end_datetime: 'End Date',
        source_of_truth: 'Source of Truth',
        users: 'User',
        action: 'Action',
      },
      fieldMatcher: {
        location_name: ['Location', 'Location Name'],
        department_name: ['Department', 'Department Name'],
        action: ['Action On Duplicate'],
      },
    },
  }

const SETTINGS_IMPORT_BUDGETS_SETUP_SECTION_DATA: SettingsImportSectionData = {
  type: ImportTypes.BUDGET_CATEGORY,
  title: messages.budgetSetupHeader,
  description: {
    message: messages.budgetSetupDescription,
  },
  href: 'https://success.procurify.com/hc/en-us/articles/14153756478743-How-to-import-Budget-Categories',
  modalTitle: messages.budgetSetupModalHeader,
  importerOptions: {
    importEndpoint: IMPORT_BUDGET_CATEGORY_URL,
    templateEndpoint: BUDGET_CATEGORY_CSV_TEMPLATE_URL,
    requiredFields: ['name'],
    optionalFields: [
      'location_name',
      'department_name',
      'account_code',
      'period',
      'customize_periods',
      'budget',
      'start_datetime',
      'end_datetime',
      'source_of_truth',
      'users',
      'action',
    ],
    fieldNameMapper: {
      name: 'Budget Name',
      location_name: 'Location Name',
      department_name: 'Department Name',
      account_code: 'Account Code',
      period: 'Period',
      customize_periods: 'Custom Period',
      budget: 'Budget',
      start_datetime: 'Start Date',
      end_datetime: 'End Date',
      source_of_truth: 'Source of Truth',
      users: 'User',
      action: 'Action',
    },
    fieldMatcher: {
      location_name: ['Location', 'Location Name'],
      department_name: ['Department', 'Department Name'],
      action: ['Action On Duplicate'],
    },
  },
}

const SETTINGS_IMPORT_BUDGET_SECTION_DATA: SettingsImportSectionData = {
  type: ImportTypes.BUDGET,
  title: messages.budgetHeader,
  description: {
    message: messages.budgetDescription,
  },
  href: 'https://success.procurify.com/hc/en-us/articles/360047107434-How-to-Import-Budgets',
  modalTitle: messages.budgetModalHeader,
  importerOptions: {
    importEndpoint: IMPORT_BUDGET_URL,
    templateEndpoint: BUDGET_CSV_TEMPLATE_URL,
    requiredFields: [
      'accountcode_code',
      'branch_name',
      'department_name',
      'budget',
      'start',
      'end',
    ],
    optionalFields: ['action_on_duplicate'],
    fieldNameMapper: {
      accountcode_code: 'Account Code',
      branch_name: 'Location Name',
      department_name: 'Department Name',
      budget: 'Budget',
      start: 'Start Date',
      end: 'End Date',
      action_on_duplicate: 'Action on Duplicate',
    },
  },
}

const SETTINGS_IMPORT_PLANNED_BUDGET_SECTION_DATA: SettingsImportSectionData = {
  type: ImportTypes.PLANNED_BUDGET,
  title: messages.plannedBudgetHeader,
  description: {
    message: messages.plannedBudgetDescription,
  },
  href: 'https://success.procurify.com/hc/en-us/articles/115010215587-How-to-Import-a-Planned-or-Scheduled-Budget-by-Importing-a-CSV-File-Detailed-Instructions',
  modalTitle: messages.plannedBudgetModalHeader,
  importerOptions: {
    importEndpoint: IMPORT_PLANNED_BUDGET_URL,
    templateEndpoint: PLANNED_BUDGET_CSV_TEMPLATE_URL,
    requiredFields: [
      'accountcode_code',
      'branch_name',
      'department_name',
      'budget',
      'start',
      'end',
    ],
    optionalFields: [],
    fieldNameMapper: {
      accountcode_code: 'Account Code',
      branch_name: 'Location Name',
      department_name: 'Department Name',
      budget: 'Budget',
      start: 'Start Date',
      end: 'End Date',
    },
  },
}

const SETTINGS_IMPORT_VENDOR_SECTION_DATA: SettingsImportSectionData = {
  type: ImportTypes.VENDOR,
  title: messages.vendorHeader,
  description: {
    message: messages.vendorDescription,
  },
  href: 'https://success.procurify.com/hc/en-us/articles/360014104094-How-to-Import-Vendors',
  modalTitle: messages.vendorModalHeader,
  importerOptions: {
    importEndpoint: IMPORT_VENDOR_URL,
    templateEndpoint: VENDOR_CSV_TEMPLATE_URL,
    requiredFields: ['name'],
    optionalFields: [
      'address_1',
      'postal_code',
      'city',
      'state_province',
      'country',
      'phone_1',
      'address_2',
      'phone_2',
      'fax',
      'email',
      'comment',
      'contact',
      'url',
      'external_id',
      'payment_terms',
      'shipping_terms',
      'shipping_method',
      'action_on_duplicate',
      'preferred',
      'payment_method_name',
      'payment_method_type',
      'payment_method_currency',
      'payable_to',
      'routing_number',
      'account_number',
      'company_name',
      'description',
      'tax',
      'is_1099_eligible',
      'payment_method_eft_institution_number',
      'payment_method_eft_transit_number',
      'payment_method_bank_account_holder_name',
      'payment_method_bank_name',
    ],
    fieldNameMapper: {
      name: 'Name',
      city: 'City',
      country: 'Country',
      address_1: 'Address One',
      address_2: 'Address Two',
      phone_1: 'Phone One',
      phone_2: 'Phone Two',
      email: 'Email',
      url: 'Website',
      fax: 'Fax',
      external_id: 'External ID',
      payment_terms: 'Payment Terms',
      shipping_terms: 'Shipping Terms',
      // 'payment_method'     : 'Payment Method',
      shipping_method: 'Shipping Method',
      postal_code: 'Zip / Postal Code',
      state_province: 'State / Province',
      // 'payment_method_ref' : 'Payment Method',
      shipping_method_ref: 'Shipping Method',
      payment_terms_ref: 'Payment Terms',
      shipping_terms_ref: 'Shipping Terms',
      action_on_duplicate: 'Action On Duplicate',
      preferred: 'Preferred',
      payment_method_name: 'Payment Method Name',
      payment_method_type: 'Payment Method Type',
      payment_method_currency: 'Payment Method Currency',
      payable_to: 'Payable To',
      routing_number: 'ACH Routing Number',
      account_number: 'ACH Account Number',
      company_name: 'ACH Company Name',
      description: 'Payment Method Description',
      tax: 'Tax',
      is_1099_eligible: 'Is the vendor 1099 eligible?',
      payment_method_eft_institution_number:
        'Payment Method EFT Institution Number',
      payment_method_eft_transit_number: 'Payment Method EFT Transit Number',
      payment_method_bank_account_holder_name:
        'Payment Method Bank Account Holder Name',
      payment_method_bank_name: 'Payment Method Bank Name',
    },
    fieldMatcher: {
      address_1: ['Address 1', 'address'],
      address_2: ['Address 2'],
      phone_1: ['Phone 1', 'phone'],
      phone_2: ['Phone 2'],
      postal_code: ['ZIP/POSTAL CODE', 'postal'],
      state_province: ['STATE/PROVINCE', 'state', 'province'],
      action_on_duplicate: ['ACTION ON DUPLICATE'],

      preferred: ['Preferred', 'PREFERRED'],

      payment_method_name: ['Payment Method Name', 'PAYMENT METHOD NAME'],
      payment_method_type: ['Payment Method Type', 'PAYMENT METHOD TYPE'],
      payment_method_currency: [
        'Payment Method Currency',
        'PAYMENT METHOD CURRENCY',
      ],
      payable_to: ['Payable To', 'PAYABLE TO'],
      routing_number: ['Routing Number', 'ROUTING NUMBER'],
      account_number: ['Account Number', 'ACCOUNT NUMBER'],
      company_name: [
        'Company Name',
        'COMPANY NAME',
        'Payee Name',
        'PAYEE NAME',
      ],
      description: ['Description', 'DESCRIPTION'],
      tax: ['Tax', 'TAX'],
      is_1099_eligible: ['1099 Eligible', '1099 ELIGIBLE'],
      payment_method_eft_institution_number: [
        'Payment Method EFT Institution Number',
        'PAYMENT METHOD EFT INSTITUTION NUMBER',
      ],
      payment_method_eft_transit_number: [
        'Payment Method EFT Transit Number',
        'PAYMENT METHOD EFT TRANSIT NUMBER',
      ],
      payment_method_bank_account_holder_name: [
        'Payment Method Bank Account Holder Name',
        'PAYMENT METHOD BANK ACCOUNT HOLDER NAME',
      ],
      payment_method_bank_name: [
        'Payment Method Bank Name',
        'PAYMENT METHOD BANK NAME',
      ],
    },
    errorMsg: {
      'Invalid pk "-1" - object does not exist.':
        ' cannot be matched. Please ensure entry exists in Procurify.',
    },
  },
}

const SETTINGS_IMPORT_CATALOG_ITEM_SECTION_DATA: SettingsImportSectionData = {
  type: ImportTypes.CATALOG_ITEM,
  title: messages.catalogItemHeader,
  description: {
    message: messages.catalogItemDescription,
  },
  href: 'https://success.procurify.com/hc/en-us/articles/360019281113-How-to-Import-Product-Catalog-Items',
  modalTitle: messages.catalogItemModalHeader,
  importerOptions: {
    importEndpoint: IMPORT_CATALOG_ITEM_URL,
    templateEndpoint: CATALOG_ITEM_CSV_TEMPLATE_URL,
    requiredFields: ['name', 'price', 'currency'],
    optionalFields: [
      'description',
      'account_code',
      'id',
      'image',
      'unit',
      'sku',
      'vendor',
    ],
    forceOptionalCustomfields: true,
    fieldNameMapper: {
      name: 'Item',
      description: 'Description',
      unit: 'Unit Type',
      sku: 'SKU',
      price: 'Unit Cost',
      currency: 'Currency',
      vendor: 'Vendor',
      account_code: 'Account Code',
      image: 'Image',
    },
    fieldMatcher: {
      name: ['product name', 'product', 'item name', 'item'],
      description: ['desc', 'info', 'information'],
      unit: ['unit type'],
      price: ['cost', 'unit cost'],
      currency: ['cur'],
      sku: [],
      vendor: [/vendor/i],
      account_code: ['Account Code'],
    },
  },
}

const SETTINGS_IMPORT_CATALOG_PERMISSION_SECTION_DATA: SettingsImportSectionData =
  {
    type: ImportTypes.CATALOG_PERMISSION,
    title: messages.catalogPermissionHeader,
    description: {
      message: messages.catalogPermissionDescription,
    },
    href: 'https://success.procurify.com/hc/en-us/articles/360047941913-How-to-Import-Catalog-Permissions',
    modalTitle: messages.catalogPermissionModalHeader,
    importerOptions: {
      importEndpoint: IMPORT_CATALOG_PERMISSION_URL,
      templateEndpoint: CATALOG_PERMISSION_CSV_TEMPLATE_URL,
      requiredFields: ['catalogitem_name', 'location_name', 'department_name'],
      optionalFields: [],
      fieldNameMapper: {
        catalogitem_name: 'Catalog Item',
        location_name: 'Location Name',
        department_name: 'Department Name',
      },
      fieldMatcher: {
        catalogitem_name: ['Catalog Item'],
        location_name: ['Location', 'Location Name'],
        department_name: ['Department', 'Department Name'],
      },
    },
  }

export const SETTINGS_IMPORT_SECTION_DATA: SettingsImportSectionDataType = {
  LOCATION: SETTINGS_IMPORT_LOCATION_SECTION_DATA,
  SHIPPING_ADDRESS: SETTINGS_IMPORT_SHIPPING_ADDRESS_SECTION_DATA,
  DEPARTMENT: SETTINGS_IMPORT_DEPARTMENT_SECTION_DATA,
  USER: SETTINGS_IMPORT_USER_SECTION_DATA,
  ROLE: SETTINGS_IMPORT_ROLE_SECTION_DATA,
  PERMISSION: SETTINGS_IMPORT_PERMISSION_SECTION_DATA,
  APPROVER: SETTINGS_IMPORT_APPROVER_SECTION_DATA,
  APPROVAL_ROUTING_GROUP: SETTINGS_IMPORT_APPROVAL_ROUTING_GROUP_SECTION_DATA,
  APPROVAL_ROUTING_GROUP_WITH_ADVANCED_TRIGGERS:
    SETTINGS_IMPORT_APPROVAL_ROUTING_GROUP_WITH_ADVANCED_TRIGGERS,
  ACCOUNT: SETTINGS_IMPORT_ACCOUNT_SECTION_DATA,
  BUDGET: SETTINGS_IMPORT_BUDGET_SECTION_DATA,
  PLANNED_BUDGET: SETTINGS_IMPORT_PLANNED_BUDGET_SECTION_DATA,
  VENDOR: SETTINGS_IMPORT_VENDOR_SECTION_DATA,
  CATALOG_ITEM: SETTINGS_IMPORT_CATALOG_ITEM_SECTION_DATA,
  CATALOG_PERMISSION: SETTINGS_IMPORT_CATALOG_PERMISSION_SECTION_DATA,
  BUDGET_CATEGORY: SETTINGS_IMPORT_BUDGET_CATEGORY_SECTION_DATA,
  BUDGETS_SETUP: SETTINGS_IMPORT_BUDGETS_SETUP_SECTION_DATA,
  ACCOUNT_CODE_LINK: SETTINGS_IMPORT_ACCOUNT_CODE_LINK_SECTION_DATA,
} as const

export const SETTINGS_IMPORT_BRANCH_SECTION_ROWS = [
  SETTINGS_IMPORT_SECTION_DATA.LOCATION,
  SETTINGS_IMPORT_SECTION_DATA.SHIPPING_ADDRESS,
  SETTINGS_IMPORT_SECTION_DATA.DEPARTMENT,
]

export const SETTINGS_IMPORT_USER_SECTION_ROWS = [
  SETTINGS_IMPORT_SECTION_DATA.USER,
  SETTINGS_IMPORT_SECTION_DATA.ROLE,
]

export const SETTINGS_IMPORT_APPROVAL_SECTION_ROWS = [
  SETTINGS_IMPORT_SECTION_DATA.APPROVAL_ROUTING_GROUP,
  SETTINGS_IMPORT_SECTION_DATA.APPROVER,
]

export const getSettingsImportApprovalSectionRows = (
  shouldIncludeAdvancedTriggers,
  approvalRoutingDescriptionMessage
) => {
  const approvalRoutingGroup = shouldIncludeAdvancedTriggers
    ? SETTINGS_IMPORT_SECTION_DATA.APPROVAL_ROUTING_GROUP_WITH_ADVANCED_TRIGGERS
    : SETTINGS_IMPORT_SECTION_DATA.APPROVAL_ROUTING_GROUP
  approvalRoutingGroup.description = {
    message: approvalRoutingDescriptionMessage,
  }
  return [approvalRoutingGroup, SETTINGS_IMPORT_SECTION_DATA.APPROVER]
}

export const SETTINGS_IMPORT_BUDGET_SECTION_ROWS = [
  SETTINGS_IMPORT_SECTION_DATA.BUDGET,
  SETTINGS_IMPORT_SECTION_DATA.PLANNED_BUDGET,
]

export const SETTINGS_IMPORT_OPTIONAL_SECTION_ROWS = [
  SETTINGS_IMPORT_SECTION_DATA.VENDOR,
  SETTINGS_IMPORT_SECTION_DATA.CATALOG_ITEM,
  SETTINGS_IMPORT_SECTION_DATA.CATALOG_PERMISSION,
]
